import {BaseAttribute, BaseAttributeOptions} from '../BaseAttribute';

/** Options for foreign-key (joined entity) attributes: shared base options plus join / filter fields */
export type FkBaseAttributeOptions = BaseAttributeOptions & {
  fkToUpdate?: string | null;
  joinPath?: unknown[] | null;
  onlyFetchDesignation?: boolean | null;
  supplementaryJoinColumns?: unknown[] | null;
  filters?: Record<string, unknown> | null;
};

function isFkModelLoader(value: unknown): value is () => any {
  return (
    typeof value === 'function' &&
    Boolean((value as {__isFkModelLoader?: boolean}).__isFkModelLoader)
  );
}

/** Base class for attributes that reference another entity (`FK_*` types, language, etc.) */
export class FkBaseAttribute extends BaseAttribute {
  private _dataObjectClass: any | null = null;
  private _dataObjectClassLoader: (() => any) | null = null;
  fkToUpdate?: string | null;
  joinPath: unknown[];
  onlyFetchDesignation?: boolean | null;
  supplementaryJoinColumns: unknown[];
  filters?: Record<string, unknown> | null;

  constructor(
    options: FkBaseAttributeOptions = {},
    dataObjectClass: any | null = null,
  ) {
    const {
      fkToUpdate = null,
      joinPath = [],
      onlyFetchDesignation = false,
      supplementaryJoinColumns = [],
      filters = null,
      ...baseOptions
    } = options;

    super(baseOptions);
    this.dataObjectClass = dataObjectClass ?? null;
    this.fkToUpdate = fkToUpdate;
    this.joinPath = joinPath ?? [];
    this.onlyFetchDesignation = onlyFetchDesignation;
    this.supplementaryJoinColumns = supplementaryJoinColumns ?? [];
    this.filters = filters ?? null;
  }

  /**
   * Model class for this FK. May be a lazy loader (fkModel) to avoid circular imports.
   */
  get dataObjectClass(): any | null {
    if (this._dataObjectClassLoader) {
      return this._dataObjectClassLoader();
    }
    return this._dataObjectClass;
  }

  set dataObjectClass(value: any | null) {
    if (isFkModelLoader(value)) {
      this._dataObjectClassLoader = value;
      this._dataObjectClass = null;
    } else {
      this._dataObjectClassLoader = null;
      this._dataObjectClass = value;
    }
  }

  // Input type that is used if attribute is used in edit mode
  get inputType(): string {
    return 'fk_entity';
  }

  // For FK attributes, we pass the data model to the table properties to be used in the header control
  get customTableProperties(): Object {
    return {
      dataObjectClass: this.dataObjectClass,
      customFilters: this.filters,
    };
  }

  /** The name of the column in the database to use for GET and POST requests */
  get dbColumn(): string {
    const fkName =
      this.dataObjectClass == null
        ? undefined
        : (this.dataObjectClass as unknown as {fkName?: string}).fkName;
    return this.fkToUpdate ?? fkName ?? this.column ?? this.name;
  }

  /** String used to query the data via postgrest */
  get selectString(): string {
    if (!this.dataObjectClass || !this.joinPath) return '';

    let str = '';
    this.joinPath.forEach((path) => (str += path + '('));
    str += this.dataObjectClass.entityColumns(
      this.onlyFetchDesignation
        ? this.dataObjectClass.joinAttrsForDesignation.concat(
            this.dataObjectClass.getAttributes(this.supplementaryJoinColumns),
          )
        : this.dataObjectClass.getAttributes(
            this.dataObjectClass.joinAttrsKey.concat(
              this.supplementaryJoinColumns,
            ),
          ),
    );
    str += ')'.repeat(this.joinPath.length);
    return `${this.name}:${str}`;
  }

  /**
   * Extracts the id of the foreign key value that is used in the database
   * Returns two values:
   * - The value sent to the database (the id of the foreign key value)
   * - The value to update the data in the UI (the foreign key object)
   */
  toDbValue(value: unknown): [unknown, unknown] {
    if (!value) return [null, null];
    if (typeof value === 'object' && value !== null && 'id' in value) {
      const fkValue = value as {id: unknown};
      return [fkValue.id, value];
    }
    return [null, null];
  }

  /** Converts the value from the database to a value that can be used in the UI */
  toLocalValue(item: Record<string, unknown>): unknown {
    let value = item[this.name];
    if (!value) return null;
    if (this.transform) {
      value = this.transform(value);
    }
    if (typeof value === 'object' && value !== null && 'id' in value) {
      return new this.dataObjectClass(value);
    }
    return null;
  }
}
